import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { ControlLabel, FormControl, FormGroup, InputGroup } from 'react-bootstrap';
import Button from 'shared/button';
import Toggle from 'components/toggle';

import { put } from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock, faSquare, faStar } from '@fortawesome/pro-solid-svg-icons';
import Teams from 'bundles/email/editor/teams/teams';
import { isEqual } from 'lodash';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function EmailNotificationsTab({
  notificationCustomizations, notificationEmailConfig, primaryBroker, propertyId, teamsUrl, teamsV2
}) {
  const initEmail = useMemo(() => notificationCustomizations?.replyToEmail || primaryBroker.email || '', []);
  const initFrom = useMemo(() => notificationCustomizations?.fromAlias || primaryBroker.name || '', []);
  const initNotificationEmailConfig = useMemo(() => notificationEmailConfig || {}, []);

  const [savedReplyToEmail, setSavedReplyToEmail] = useState(initEmail);
  const [savedFromAlias, setSavedFromAlias] = useState(initFrom);
  const [savedNotificationEmailConfig, setSavedNotificationEmailConfig] = useState(initNotificationEmailConfig);

  const [formData, setFormData] = useState({
    replyToEmail: initEmail,
    fromAlias: initFrom,
    notificationEmailConfig: initNotificationEmailConfig
  });

  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const validEmail = useMemo(() => {
    return EMAIL_REGEX.test(formData.replyToEmail);
  }, [formData.replyToEmail]);

  const dirtyForm = useMemo(() => {
    return (!!formData.replyToEmail && formData.replyToEmail != savedReplyToEmail) ||
      (!!formData.fromAlias && formData.fromAlias != savedFromAlias) ||
      !isEqual(formData.notificationEmailConfig, savedNotificationEmailConfig);
  }, [formData, savedFromAlias, savedReplyToEmail, notificationEmailConfig]);

  const canSubmit = dirtyForm && validEmail;

  const canSetToPrimary = useMemo(() => {
    return (formData.replyToEmail != primaryBroker.email) || (formData.fromAlias != primaryBroker.name);
  }, [primaryBroker, formData]);

  const setToPrimaryBroker = () => {
    setFormData({
      ...formData,
      fromAlias: primaryBroker?.name,
      replyToEmail: primaryBroker?.email
    });
  };

  const changesSaved = () => {
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 5000);
  };

  const saveChanges = () => {
    if (!canSubmit) return;
    
    setSaving(true);
    const { replyToEmail, fromAlias } = formData;
    const params = {
      emailNotificationCustomizations: { replyToEmail, fromAlias },
      notificationEmailConfig: formData.notificationEmailConfig
    };

    put(`/properties/${propertyId}`, params).then(() => {
      setSavedFromAlias(fromAlias);
      setSavedReplyToEmail(replyToEmail);
      setSavedNotificationEmailConfig(formData.notificationEmailConfig);
      changesSaved();
    });
  };

  const renderBullet = (label, icon) => {
    return (
      <div className="my-3 d-flex align-items-center bold">
        <div className="fa-stack fa-lg mr-2">
          <FontAwesomeIcon className="fa-stack-2x" color="whitesmoke" icon={faSquare}/>
          <FontAwesomeIcon className="fa-stack-1x" icon={icon}/>
        </div>
        {label}
      </div>
    );
  };

  const notificationEmailConfigValue = (key) => {
    return (key in formData.notificationEmailConfig) ? formData.notificationEmailConfig[key] : true;
  };

  const updateNotificationEmailConfig = (key, value) => {
    const newNotificationEmailConfig = { ...formData.notificationEmailConfig, [key]: value };
    setFormData({
      ...formData,
      notificationEmailConfig: newNotificationEmailConfig
    });
  };

  const renderAutomaticEmailToggle = (key, label) => {
    return (
      <div className="mb-4 d-flex align-items-center gap-3">
        <Toggle
          checked={notificationEmailConfigValue(key)}
          disabled={saving}
          theme="ds"
          onChange={e => updateNotificationEmailConfig(key, e.target.checked)}
        />
        <span className="fw-semibold">
          {label}
        </span>
      </div>
    );
  };

  return (
    <div>
      {saved && <div className="text-success w-100 text-center">Changes Saved</div>}
      <div>
        <h4>Automatic Emails</h4>
        <div>
          {renderAutomaticEmailToggle('docAccessLevelsChanged', 'There are new documents Available')}
          {renderAutomaticEmailToggle('vaultAccessApproved', 'Waitlist access is approved')}
        </div>
      </div>
      <div className="mb-4">
        Buildout automatically sends emails to your users when:
        {renderBullet('There are new documents available', faStar)}
        {renderBullet('Waitlist access is approved', faLock)}
        Edit the information Buildout uses when we send emails on your behalf for document-related notices.
      </div>
      <FormGroup className="my-3">
        <ControlLabel>From Alias</ControlLabel>
        <InputGroup className="w-100">
          <FormControl
            disabled={saving}
            type="text"
            value={formData.fromAlias}
            onChange={e => setFormData({ ...formData, fromAlias: e.target.value })}
          />
          {formData.fromAlias && formData.fromAlias == savedFromAlias && (
            <InputGroup.Addon>
              <FontAwesomeIcon className="text-success" icon={faCheck}/>
            </InputGroup.Addon>
          )}
        </InputGroup>
        <div className="text-muted">
          Your emails will be delivered using this name
        </div>
      </FormGroup>
      <FormGroup className="my-3">
        <ControlLabel>Reply-To Email</ControlLabel>
        <InputGroup className="w-100">
          <FormControl
            disabled={saving}
            required
            type="email"
            value={formData.replyToEmail}
            onChange={e => setFormData({ ...formData, replyToEmail: e.target.value })}
          />
          {formData.replyToEmail && formData.replyToEmail == savedReplyToEmail && (
            <InputGroup.Addon>
              <FontAwesomeIcon className="text-success" icon={faCheck}/>
            </InputGroup.Addon>
          )}
          {!validEmail && dirtyForm && (
            <p className="text-danger">Enter a valid email address</p>
          )}
        </InputGroup>
        <div className="text-muted">
          Replies to your emails will go to this address
        </div>
      </FormGroup>
      <div className="text-right">
        <Button.Secondary className="mr-2" disabled={!canSetToPrimary} onClick={setToPrimaryBroker}>
          Use Primary Broker
        </Button.Secondary>
        <Button.Primary disabled={!canSubmit} onClick={saveChanges}>
          Save Changes
        </Button.Primary>
      </div>

      {teamsV2 && <Teams searchUrl="/email_teams" teamsUrl={teamsUrl} teamsV2={teamsV2}/>}
    </div>
  );
}

EmailNotificationsTab.propTypes = {
  notificationCustomizations: PropTypes.shape({
    replyToEmail: PropTypes.string,
    fromAlias: PropTypes.string
  }),
  notificationEmailConfig: PropTypes.shape({
    docAccessLevelsChanged: PropTypes.bool,
    vaultAccessApproved: PropTypes.bool
  }),
  primaryBroker: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  propertyId: PropTypes.number.isRequired,
  teamsUrl: PropTypes.string.isRequired,
  teamsV2: PropTypes.bool
};

export default EmailNotificationsTab;
